import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';

function JoinTeam() {
  const { token } = useParams();
  const navigate = useNavigate();

  // Invitation validation state
  const [invitation, setInvitation] = useState(null);
  const [validating, setValidating] = useState(true);
  const [validationError, setValidationError] = useState('');

  // Form state
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    password: '',
    confirmPassword: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  // Validate token on mount
  useEffect(() => {
    const validateToken = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/team/invitation/${token}`
        );
        setInvitation(response.data);
      } catch (err) {
        setValidationError(
          err.response?.data?.error || 'Invalid or expired invitation link'
        );
      } finally {
        setValidating(false);
      }
    };

    if (token) {
      validateToken();
    } else {
      setValidationError('No invitation token provided');
      setValidating(false);
    }
  }, [token]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setSubmitError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');

    // Validation
    if (!formData.firstName.trim() || !formData.lastName.trim()) {
      setSubmitError('First and last name are required');
      return;
    }

    if (formData.password.length < 8) {
      setSubmitError('Password must be at least 8 characters');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setSubmitError('Passwords do not match');
      return;
    }

    setSubmitting(true);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/team/accept`,
        {
          token,
          firstName: formData.firstName.trim(),
          lastName: formData.lastName.trim(),
          password: formData.password,
        }
      );

      // Auto-login with the returned token
      if (response.data.token) {
        localStorage.setItem('token', response.data.token);
        axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;
        // Refresh auth context
        window.location.href = '/dashboard';
      } else {
        navigate('/login');
      }
    } catch (err) {
      setSubmitError(
        err.response?.data?.error || 'Failed to create account. Please try again.'
      );
      setSubmitting(false);
    }
  };

  const formatRole = (role) => {
    const roleMap = {
      admin: 'Admin',
      crew_lead: 'Crew Lead',
      driver: 'Driver',
    };
    return roleMap[role] || role;
  };

  const getRoleBadgeClass = (role) => {
    const classMap = {
      admin: 'badge-admin',
      crew_lead: 'badge-crew-lead',
      driver: 'badge-driver',
    };
    return classMap[role] || 'badge-secondary';
  };

  // Loading state
  if (validating) {
    return (
      <div className="join-team-page">
        <div className="join-team-container">
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>Validating invitation...</p>
          </div>
        </div>
        <style>{styles}</style>
      </div>
    );
  }

  // Error state
  if (validationError) {
    return (
      <div className="join-team-page">
        <div className="join-team-container">
          <div className="error-state">
            <div className="error-icon">!</div>
            <h2>Invitation Error</h2>
            <p>{validationError}</p>
            <button className="btn btn-primary" onClick={() => navigate('/login')}>
              Go to Login
            </button>
          </div>
        </div>
        <style>{styles}</style>
      </div>
    );
  }

  // Form state
  return (
    <div className="join-team-page">
      <div className="join-team-container">
        <div className="join-team-header">
          <h1>Join {invitation.companyName}</h1>
          <p>
            You've been invited to join as a{' '}
            <span className={`badge ${getRoleBadgeClass(invitation.role)}`}>
              {formatRole(invitation.role)}
            </span>
          </p>
        </div>

        <div className="join-team-card">
          <div className="invited-email">
            <span className="label">Invited Email:</span>
            <span className="email">{invitation.email}</span>
          </div>

          <form onSubmit={handleSubmit}>
            {submitError && (
              <div className="alert alert-error">{submitError}</div>
            )}

            <div className="form-row">
              <div className="form-group">
                <label className="form-label" htmlFor="firstName">
                  First Name
                </label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  className="form-input"
                  value={formData.firstName}
                  onChange={handleChange}
                  required
                  autoFocus
                />
              </div>

              <div className="form-group">
                <label className="form-label" htmlFor="lastName">
                  Last Name
                </label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  className="form-input"
                  value={formData.lastName}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="password">
                Password
              </label>
              <input
                type="password"
                id="password"
                name="password"
                className="form-input"
                value={formData.password}
                onChange={handleChange}
                required
                minLength={8}
                placeholder="At least 8 characters"
              />
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="confirmPassword">
                Confirm Password
              </label>
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                className="form-input"
                value={formData.confirmPassword}
                onChange={handleChange}
                required
              />
            </div>

            <button
              type="submit"
              className="btn btn-primary btn-block"
              disabled={submitting}
            >
              {submitting ? 'Creating Account...' : 'Create Account & Join Team'}
            </button>
          </form>
        </div>

        <div className="join-team-footer">
          <p>
            Already have an account?{' '}
            <a href="/login">Sign in</a>
          </p>
        </div>
      </div>
      <style>{styles}</style>
    </div>
  );
}

const styles = `
  .join-team-page {
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, #1e3a5f 0%, #0d1b2a 100%);
    padding: 20px;
  }

  .join-team-container {
    width: 100%;
    max-width: 480px;
  }

  .join-team-header {
    text-align: center;
    margin-bottom: 24px;
    color: white;
  }

  .join-team-header h1 {
    font-size: 1.75rem;
    margin-bottom: 8px;
    color: #ffffff;
  }

  .join-team-header p {
    color: rgba(255, 255, 255, 0.8);
    font-size: 1rem;
  }

  .join-team-card {
    background: white;
    border-radius: 12px;
    padding: 32px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  }

  .invited-email {
    background: #f0f4f8;
    padding: 12px 16px;
    border-radius: 8px;
    margin-bottom: 24px;
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .invited-email .label {
    font-size: 0.75rem;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  .invited-email .email {
    font-weight: 600;
    color: #1e293b;
  }

  .form-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
  }

  .form-group {
    margin-bottom: 16px;
  }

  .form-label {
    display: block;
    margin-bottom: 6px;
    font-weight: 500;
    color: #374151;
  }

  .form-input {
    width: 100%;
    padding: 10px 14px;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    font-size: 1rem;
    transition: border-color 0.2s, box-shadow 0.2s;
  }

  .form-input:focus {
    outline: none;
    border-color: #2563eb;
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
  }

  .btn-block {
    width: 100%;
    padding: 12px;
    font-size: 1rem;
    margin-top: 8px;
  }

  .btn-primary {
    background: #2563eb;
    color: white;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    font-weight: 500;
    transition: background 0.2s;
  }

  .btn-primary:hover:not(:disabled) {
    background: #1d4ed8;
  }

  .btn-primary:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .alert {
    padding: 12px 16px;
    border-radius: 6px;
    margin-bottom: 16px;
  }

  .alert-error {
    background: #fef2f2;
    color: #dc2626;
    border: 1px solid #fecaca;
  }

  .join-team-footer {
    text-align: center;
    margin-top: 24px;
    color: rgba(255, 255, 255, 0.7);
  }

  .join-team-footer a {
    color: #60a5fa;
    text-decoration: none;
  }

  .join-team-footer a:hover {
    text-decoration: underline;
  }

  .loading-state,
  .error-state {
    background: white;
    border-radius: 12px;
    padding: 48px 32px;
    text-align: center;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  }

  .loading-spinner {
    width: 48px;
    height: 48px;
    border: 4px solid #e5e7eb;
    border-top-color: #2563eb;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin: 0 auto 16px;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  .error-icon {
    width: 64px;
    height: 64px;
    background: #fef2f2;
    color: #dc2626;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 2rem;
    font-weight: bold;
    margin: 0 auto 16px;
  }

  .error-state h2 {
    color: #1e293b;
    margin-bottom: 8px;
  }

  .error-state p {
    color: #64748b;
    margin-bottom: 24px;
  }

  /* Role badges */
  .badge {
    display: inline-block;
    padding: 4px 10px;
    border-radius: 9999px;
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: uppercase;
  }

  .badge-admin {
    background: #dbeafe;
    color: #1d4ed8;
  }

  .badge-crew-lead {
    background: #dcfce7;
    color: #15803d;
  }

  .badge-driver {
    background: #f3f4f6;
    color: #4b5563;
  }

  @media (max-width: 480px) {
    .form-row {
      grid-template-columns: 1fr;
    }

    .join-team-card {
      padding: 24px 20px;
    }
  }
`;

export default JoinTeam;
